import { Button } from "@/components/ui/button";
import { ArrowRight, Star, Sparkles, Globe, CheckCircle, Film, Play, Phone } from "lucide-react";
import Navbar from "@/components/Navbar";
import { useIsMobile } from "@/hooks/use-mobile";
import { useState, useEffect, memo } from "react";
import { CONTACT } from "@/config/contact";

// Memoized static tags to prevent re-renders
const tags = [
{ icon: Globe, text: "Aachen & ganz NRW" },
{ icon: Film, text: "Imagefilme · Testimonials · Reels" },
{ icon: Sparkles, text: "KI-gestützte Workflows" },
{ icon: CheckCircle, text: "Seit 2020 · 50+ Projekte" }];


const Hero = memo(() => {
  const [loadVideo, setLoadVideo] = useState(false);
  const isMobile = useIsMobile();

  // Defer video loading: on desktop after idle, on mobile on tap
  useEffect(() => {
    if (isMobile) return;
    // Use requestIdleCallback for non-blocking deferred load
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(() => setLoadVideo(true), { timeout: 3000 });
    } else {
      setTimeout(() => setLoadVideo(true), 2500);
    }
  }, [isMobile]);
  return <section className="relative min-h-screen flex flex-col md:flex-row md:items-center overflow-hidden">
      {/* Video Background - Full height on mobile with fade */}
      <div
      className={`${isMobile ? 'relative h-[50vh] w-full' : 'absolute inset-0'} z-0`}
      style={{
        transform: 'translate3d(0,0,0)',
        contain: 'strict',
        isolation: 'isolate'
      }}
      onClick={() => !loadVideo && setLoadVideo(true)}>

        {/* Solid background for fastest paint */}
        <div className="absolute inset-0 bg-background" />
        {/* Thumbnail fallback for LCP optimization - simplified for speed */}
        <img
        src="https://vumbnail.com/1153425389.jpg"
        alt=""
        className={`absolute top-1/2 left-1/2 min-w-full min-h-full w-auto h-auto -translate-x-1/2 -translate-y-1/2 object-cover ${loadVideo ? 'opacity-0' : 'opacity-100'}`}
        style={{ transition: 'opacity 0.3s' }}
        width="1280"
        height="720"
        decoding="async"
        fetchPriority="high" />

        {/* Mobile Play Button Overlay */}
        {isMobile && !loadVideo &&
      <div className="absolute inset-0 flex items-center justify-center z-20">
            <div className="bg-background/80 backdrop-blur-sm rounded-full p-5 shadow-lg">
              <Play className="w-8 h-8 text-accent fill-accent" />
            </div>
          </div>
      }
        {/* Only load iframe when loadVideo is true */}
        {loadVideo &&
      <iframe
        src="https://player.vimeo.com/video/1153425389?autoplay=1&loop=1&muted=1&autopause=0&controls=0&background=1&playsinline=1&quality=auto&dnt=1&transparent=0"
        className="absolute top-1/2 left-1/2 min-w-full min-h-full w-auto h-auto -translate-x-1/2 -translate-y-1/2 pointer-events-none"
        style={{
          border: 'none',
          aspectRatio: '16/9',
          transform: 'translate(-50%, -50%) translate3d(0,0,0)',
          willChange: 'transform',
          backfaceVisibility: 'hidden',
          contain: 'layout style paint'
        }}
        allow="autoplay; fullscreen; picture-in-picture"
        title="Videoproduktion Aachen - Imagefilm und KI-Videos" />

      }
        {/* Desktop gradient overlay */}
        <div className="hidden md:block absolute inset-0 bg-gradient-to-l from-transparent via-background/40 to-background pointer-events-none" style={{
        transform: 'translate3d(0,0,0)'
      }}></div>
        {/* Mobile fade to black at bottom */}
        <div className="md:hidden absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black pointer-events-none" style={{
        transform: 'translate3d(0,0,0)'
      }}></div>
      </div>

      {/* Navigation */}
      <Navbar variant="transparent" />

      {/* Content - Below video on mobile, left positioned on desktop */}
      <div className={`relative z-10 container mx-auto px-4 md:px-8 max-w-7xl ${isMobile ? 'pt-6 pb-12 bg-black' : 'pt-16 md:pt-24'}`}>
        <div className="max-w-2xl animate-slide-up md:ml-12">
          {/* Eyebrow Badge */}
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-accent/10 border border-accent/30 rounded-full">
            <ArrowRight className="w-4 h-4 text-accent" />
            <span className="text-accent font-medium text-sm">Aivinity · Aachen &amp; NRW · ehemals JC Productions</span>
          </div>

          {/* Main Headline - keyword-optimized for SEO */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-normal mb-6 tracking-tight leading-[1.1]">
            Videoproduktion aus Aachen.<br />
            <span className="font-serif italic text-accent">AI-first. Real. Wirksam.</span>
          </h1>

          <p className="text-base md:text-lg text-foreground/70 mb-8 max-w-xl leading-relaxed">
            Imagefilme, Testimonials, KI-Filme, Reels &amp; Social Media Management — für Unternehmen in Aachen, Köln, Düsseldorf und ganz NRW.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 mb-6">
            <Button className="group bg-accent hover:bg-accent/90 text-black font-bold text-base px-8 py-4 h-auto rounded-lg shadow-[0_0_20px_rgba(134,239,172,0.3)] hover:shadow-[0_0_30px_rgba(134,239,172,0.5)] transition-all duration-300" onClick={() => window.location.href = '/termin'}>
              <span className="font-bold">Jetzt starten</span>
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>

            <Button variant="outline" className="group border-2 border-foreground/20 hover:border-foreground/40 hover:bg-foreground/5 text-foreground font-medium text-base px-8 py-4 h-auto rounded-lg transition-all duration-300" onClick={() => document.getElementById('work')?.scrollIntoView({
            behavior: 'smooth'
          })}>
              Arbeiten ansehen
            </Button>
          </div>

          {/* Direct call — regional trust signal */}
          <a
            href={`tel:${CONTACT.phone}`}
            className="inline-flex items-center gap-2 mb-8 text-sm text-foreground/80 hover:text-accent transition-colors"
          >
            <Phone className="w-4 h-4" />
            <span>
              Lieber direkt anrufen? <span className="font-semibold text-foreground">{CONTACT.phoneDisplay}</span>
            </span>
          </a>

          {/* Tags Row - Static for faster LCP */}
          <div className="flex flex-wrap gap-2 md:gap-3 mb-8">
            {tags.map((tag, index) =>
          <div
            key={index}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-foreground/5 border border-foreground/10 rounded-full text-sm text-foreground/70">

                <tag.icon className="w-3.5 h-3.5" />
                <span>{tag.text}</span>
              </div>
          )}
          </div>

          {/* Google Review Badge */}
          <div className="flex items-center gap-3 animate-fade-in">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 md:w-5 md:h-5 fill-accent text-accent" />)}
            </div>
            <div className="text-foreground/90">
              <span className="font-bold text-sm md:text-base">4.9/5</span>
              <span className="text-xs md:text-sm ml-1 opacity-80">auf Google</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom fade to black - only on desktop */}
      <div className="hidden md:block absolute bottom-0 left-0 right-0 h-64 bg-gradient-to-t from-black via-black/80 to-transparent pointer-events-none"></div>
    </section>;
});

Hero.displayName = 'Hero';
export default Hero;